import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, List, Modal, Row } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { getObjectValues, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { WEB_SOCKET_ENTITY_TYPES } from '../../utils/WebSocketUtils';
import { debounce } from '../../utils/PerformanceUtils';
import { formatUserContact } from '../../utils/DataFormatterUtils';
import {
	fetchWalkthroughNotesForBuyer,
	fetchWalkthroughNotesForSupplier,
	appendWalkthroughNotesForBuyer,
	appendWalkthroughNotesForSupplier,
	appendWalkthroughNotesBulkForBuyer,
	appendWalkthroughNotesBulkForSupplier,
	walkthroughsRestCrudThunksForBuyer,
	walkthroughsRestCrudThunksForSupplier,
} from '../../thunks/walkthroughs_thunks';
import {
	fetchAllUsersForWalkthroughs,
	addBuyerSubscribersForWalkthroughs,
	addSupplierSubscribersForWalkthroughs,
	removeBuyerSubscribersForWalkthroughs,
	removeSupplierSubscribersForWalkthroughs,
} from '../../thunks/walkthroughs_subscribers_thunks';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import AvatarGroup from '../avatar_group/AvatarGroup';
import SelectUsers from '../select_users/SelectUsers';
import RemoveSubscriberButton from '../work_orders_detail_notes_page/RemoveSubscriberButton';
import CommonNotesChatPage from '../common_notes_chat_page/CommonNotesChatPage';
import { walkthroughNoteChatUploadCustomRequest } from '../../thunks/file_thunks';
import CommonNoteInputForm from '../common_note_input_form/CommonNoteInputForm';
import { withUserType } from '../withUserType';
import WalkthroughTaskSummaryDisplay from '../walkthrough_task_summary_display/WalkthroughTaskSummaryDisplay';

const WS_CONFIG = {
	entityIdName: 'walkthroughId',
	entityType: WEB_SOCKET_ENTITY_TYPES.WALKTHROUGH_NOTES,
	newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WALKTHROUGH,
};

const WalkthroughsDetailNotesPage = ({
	walkthrough,
	currentUser,
	match,
	location,
	history,
	messages,
	searched_subscribers,
	fetchMessages,
	appendNotes,
	appendNotesBulk: appendNotesBulkAction,
	userType,
	fetchUsers,
	addBuyerSubscribers,
	addSupplierSubscribers,
	removeBuyerSubscriber,
	removeSupplierSubscriber,
	getWalkthrough,
}) => {
	const walkthroughId = match.params.id;

	const [subscriberModalVisible, setSubscriberModalVisible] = useState(false);
	const [addSubscriberModalVisible, setAddSubscriberModalVisible] = useState(false);
	const [search, setSearch] = useState('');
	const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
	const [selectValue, setSelectValue] = useState<any[]>([]);
	const [addingSubscribers, setAddingSubscribers] = useState(false);
	// The task this note is being attached to (resolved from the ?siteSurveyTaskTitleId deep link
	// set by the task's "Send Note" button). Dismissible; sent along with the note when present.
	const [referencedTask, setReferencedTask] = useState<any>(null);

	// Look up the referenced task from the walkthrough's answers once both the URL param and the
	// walkthrough are available.
	useEffect(() => {
		const taskTitleId = new URLSearchParams(nullSafeGetOrElse('search', location, '')).get(
			'siteSurveyTaskTitleId'
		);
		if (!taskTitleId) return;
		const taskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
		const task = taskAnswers.find(
			(answer) => String(nullSafeGet('titleId', answer)) === String(taskTitleId)
		);
		if (task) setReferencedTask(task);
	}, [location, walkthrough]);

	const handleDismissReferencedTask = useCallback(() => {
		setReferencedTask(null);
		history.replace(`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/notes`);
	}, [history, userType, walkthroughId]);

	const fetchNotes = useCallback(() => {
		return fetchMessages(walkthroughId);
	}, [fetchMessages, walkthroughId]);

	// When a task chip is displayed over the chat box (the referenced task, set from the
	// ?siteSurveyTaskTitleId deep link), stamp its titleId onto the outgoing note so the backend
	// persists the association and the note can later render its referenced task. The chip is
	// dismissed synchronously here — the moment send is hit, not after the request resolves — so it
	// disappears immediately; the id was already captured above, so this note still carries it.
	const appendNote = useCallback(
		(note: any) => {
			const taggedNote = referencedTask
				? { ...note, siteSurveyTaskTitleId: nullSafeGet('titleId', referencedTask) }
				: note;
			if (referencedTask) handleDismissReferencedTask();
			return appendNotes({ id: walkthroughId, note: taggedNote });
		},
		[appendNotes, walkthroughId, referencedTask, handleDismissReferencedTask]
	);

	// Same body as appendNote, one request for the whole photo batch.
	const appendNotesBulk = useCallback(
		(notes: any[]) => {
			const taggedNotes = referencedTask
				? notes.map((note) => ({
						...note,
						siteSurveyTaskTitleId: nullSafeGet('titleId', referencedTask),
				  }))
				: notes;
			if (referencedTask) handleDismissReferencedTask();
			return appendNotesBulkAction({ id: walkthroughId, notes: taggedNotes });
		},
		[appendNotesBulkAction, walkthroughId, referencedTask, handleDismissReferencedTask]
	);

	// Index the walkthrough's task answers by titleId so a persisted note carrying a
	// siteSurveyTaskTitleId can resolve and render its referenced task.
	const taskAnswersByTitleId = useMemo(() => {
		const lookup: Record<string, any> = {};
		nullSafeGetOrElse('taskAnswers', walkthrough, []).forEach((answer) => {
			const titleId = nullSafeGet('titleId', answer);
			if (titleId) lookup[String(titleId)] = answer;
		});
		return lookup;
	}, [walkthrough]);

	const renderReferencedTaskChip = useCallback(
		() => (
			// Rendered ABOVE the chat input (outside the box) via ChatInput's renderAboveInput slot, which
			// dynamically aligns this banner's width/left to the textarea box. Styled as a grey banner.
			<WalkthroughTaskSummaryDisplay
				record={referencedTask}
				walkthrough={walkthrough}
				userType={userType}
				showGroupPrefix
				truncateTitle
				wrapperClassName="rounded bg-grey-3 px-3 py-2"
				onDismiss={handleDismissReferencedTask}
			/>
		),
		[referencedTask, walkthrough, userType, handleDismissReferencedTask]
	);

	const renderNoteTask = useCallback(
		(note: any) => {
			const taskTitleId = nullSafeGet('siteSurveyTaskTitleId', note);
			if (!taskTitleId) return null;
			const task = taskAnswersByTitleId[String(taskTitleId)];
			if (!task) return null;
			return (
				<WalkthroughTaskSummaryDisplay
					record={task}
					walkthrough={walkthrough}
					userType={userType}
					showGroupPrefix
					showAnswer
					showCounts
					showGoToTask
				/>
			);
		},
		[taskAnswersByTitleId, walkthrough, userType]
	);

	// Bind walkthroughId so ChatUploader (which passes workOrderId) maps to the correct param
	const walkthroughUploadRequest = useCallback(
		(args: any) =>
			walkthroughNoteChatUploadCustomRequest({
				...args,
				walkthroughId: args.workOrderId || walkthroughId,
			}),
		[walkthroughId]
	);

	const subscribers = (walkthrough.buyerSubscribers || []).concat(
		walkthrough.supplierSubscribers || []
	);

	const filteredUsers = getObjectValues(searched_subscribers.records).map((el) =>
		formatUserContact(el)
	);

	const handleSubscriberRemoved = useCallback(
		(item: any) => () => {
			// The promise is returned so RemoveSubscriberButton can show a spinner until the
			// removal and the follow-up walkthrough refetch have both settled.
			if (item.contactType === ROLE_TYPES.BUYER) {
				return removeBuyerSubscriber(
					{ id: parseInt(walkthroughId), removeBuyerSubscriberEmails: [item.email] },
					walkthrough
				).then(() => getWalkthrough(walkthroughId));
			}
			return removeSupplierSubscriber(
				{ id: parseInt(walkthroughId), removeSupplierSubscriberEmails: [item.email] },
				walkthrough
			).then(() => getWalkthrough(walkthroughId));
		},
		[removeBuyerSubscriber, removeSupplierSubscriber, walkthroughId, walkthrough, getWalkthrough]
	);

	const handleAddSubscribers = useCallback(() => {
		setAddingSubscribers(true);
		const promises = [];
		const buyerEmails = selectedUsers.filter((u) => u.contactType === 'buyer').map((u) => u.email);
		const supplierEmails = selectedUsers
			.filter((u) => u.contactType === 'supplier')
			.map((u) => u.email);
		if (buyerEmails.length > 0) {
			promises.push(
				addBuyerSubscribers(
					{ id: parseInt(walkthroughId), addBuyerSubscriberEmails: buyerEmails },
					walkthrough
				)
			);
		}
		if (supplierEmails.length > 0) {
			promises.push(
				addSupplierSubscribers(
					{ id: parseInt(walkthroughId), addSupplierSubscriberEmails: supplierEmails },
					walkthrough
				)
			);
		}
		Promise.all(promises).then(() => {
			setSelectValue([]);
			setAddingSubscribers(false);
			setAddSubscriberModalVisible(false);
			getWalkthrough(walkthroughId);
		});
	}, [
		selectedUsers,
		walkthroughId,
		walkthrough,
		addBuyerSubscribers,
		addSupplierSubscribers,
		getWalkthrough,
	]);

	const handleOnChange = useCallback((val: any) => {
		const updatedVal = Array.isArray(val) ? val.map((v) => JSON.parse(v)) : JSON.parse(val);
		setSelectValue(updatedVal.map((v) => JSON.stringify(v)));
		setSelectedUsers(updatedVal);
	}, []);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFetchUsers = useCallback(
		debounce(
			(searchText: string) => {
				fetchUsers(searchText && searchText.trim(), walkthroughId);
			},
			400,
			false
		),
		[fetchUsers, walkthroughId]
	);

	const handleSearchDebounced = useCallback(
		(value = '') => {
			const searchText = value && value.trim();
			setSearch(searchText);
			debouncedFetchUsers(searchText);
		},
		[debouncedFetchUsers]
	);

	// @-mention autocomplete in the note input must hit the walkthrough subscriber endpoint,
	// not the work-order one the shared ChatInput defaults to.
	const fetchMentionUsers = useCallback(
		(searchText: string) => fetchUsers(searchText && searchText.trim(), walkthroughId),
		[fetchUsers, walkthroughId]
	);

	const renderSubscribers = useCallback(
		() => (
			<>
				<div style={{ textAlign: 'right', padding: '0', height: '0px' }}>
					<Modal
						width={600}
						title={
							<div>
								<span>Walkthrough {walkthroughId}</span>
								<Button
									style={{ float: 'right', marginRight: '32px', marginTop: '-4px' }}
									onClick={() => {
										setSubscriberModalVisible(false);
										setAddSubscriberModalVisible(true);
										setSearch('');
										setSelectedUsers([]);
									}}
									type={'primary'}
								>
									Add People
								</Button>
							</div>
						}
						footer={null}
						closable={true}
						onCancel={() => setSubscriberModalVisible(false)}
						visible={subscriberModalVisible}
					>
						<List
							itemLayout="horizontal"
							dataSource={subscribers}
							renderItem={(item: any) => (
								<List.Item>
									<List.Item.Meta
										avatar={
											<ContactAvatar
												hidePopover={true}
												placement="bottomRight"
												userType={userType}
												contact={item}
											/>
										}
										// @ts-ignore
										title={`${item.nameGiven} ${item.nameFamily}`}
										// @ts-ignore
										description={item.email}
									/>
									<RemoveSubscriberButton
										subscriber={item}
										onRemove={handleSubscriberRemoved}
										workOrder={walkthrough}
									/>
								</List.Item>
							)}
						/>
					</Modal>
					<Modal
						width={600}
						title={
							<div>
								<span>Add Someone to Walkthrough {walkthroughId}</span>
							</div>
						}
						footer={
							<div style={{ display: 'block', width: '100%' }}>
								<Button loading={addingSubscribers} onClick={handleAddSubscribers} type={'primary'}>
									Add Subscribers
								</Button>
							</div>
						}
						closable={true}
						onCancel={() => setAddSubscriberModalVisible(false)}
						visible={addSubscriberModalVisible}
					>
						<Row key={0}>
							{/* @ts-ignore */}
							<SelectUsers
								mode="multiple"
								style={{ width: '100%' }}
								placeholder="Begin to type a name or email to start searching"
								onSearch={handleSearchDebounced}
								onFocus={() => handleSearchDebounced()}
								onChange={handleOnChange}
								search={search}
								selectValue={selectValue}
								filteredUsers={filteredUsers}
								disabledUsers={subscribers}
							/>
						</Row>
					</Modal>
				</div>
				{/* @ts-ignore */}
				<AvatarGroup
					avatars={subscribers}
					onClick={() => setSubscriberModalVisible(true)}
					onAddClick={() => {
						setAddSubscriberModalVisible(true);
						setSearch('');
						setSelectedUsers([]);
					}}
				/>
			</>
		),
		[
			walkthroughId,
			subscribers,
			subscriberModalVisible,
			addSubscriberModalVisible,
			addingSubscribers,
			search,
			selectValue,
			filteredUsers,
			handleSubscriberRemoved,
			handleAddSubscribers,
			handleSearchDebounced,
			handleOnChange,
			walkthrough,
			userType,
		]
	);

	const renderInputForm = useCallback(
		({ handleTyping, onSendNote, typingDetails, newNoteCount, onRefresh }) => {
			return (
				<CommonNoteInputForm
					appendNote={appendNote}
					appendNotesBulk={appendNotesBulk}
					currentUser={currentUser}
					workOrder={walkthrough}
					onTyping={handleTyping}
					onSendNote={onSendNote}
					typingDetails={typingDetails}
					newNoteCount={newNoteCount}
					onRefresh={onRefresh}
					customUploadRequest={walkthroughUploadRequest}
					fetchMentionUsers={fetchMentionUsers}
					renderAboveInput={referencedTask ? renderReferencedTaskChip : undefined}
				/>
			);
		},
		[
			appendNote,
			appendNotesBulk,
			currentUser,
			walkthrough,
			walkthroughUploadRequest,
			fetchMentionUsers,
			referencedTask,
			renderReferencedTaskChip,
		]
	);

	return (
		<CommonNotesChatPage
			entityId={String(walkthroughId)}
			fetchNotes={fetchNotes}
			userType={userType}
			heightSelector="walkthrough-detail-header"
			wsConfig={WS_CONFIG}
			currentUser={currentUser}
			messages={messages}
			renderSubscribers={renderSubscribers}
			renderInputForm={renderInputForm}
			renderNoteExtra={renderNoteTask}
		/>
	);
};

const mapStateToProps = (state) => ({
	walkthrough: state.walkthroughs.detail,
	messages: state.messages,
	currentUser: state.session.currentUser,
	searched_subscribers: state.searched_subscribers,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchMessages: (walkthroughId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchWalkthroughNotesForSupplier(walkthroughId)
				: fetchWalkthroughNotesForBuyer(walkthroughId)
		),
	appendNotes: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? appendWalkthroughNotesForSupplier(entity)
				: appendWalkthroughNotesForBuyer(entity)
		),
	appendNotesBulk: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? appendWalkthroughNotesBulkForSupplier(entity)
				: appendWalkthroughNotesBulkForBuyer(entity)
		),
	getWalkthrough: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.readOne(id)
				: walkthroughsRestCrudThunksForBuyer.readOne(id)
		),
	fetchUsers: (search, walkthroughId) =>
		dispatch(fetchAllUsersForWalkthroughs(search, walkthroughId)(ownProps.userType)()),
	addBuyerSubscribers: (obj, walkthrough) =>
		dispatch(addBuyerSubscribersForWalkthroughs(ownProps.userType)(obj)(walkthrough)),
	addSupplierSubscribers: (obj, walkthrough) =>
		dispatch(addSupplierSubscribersForWalkthroughs(ownProps.userType)(obj)(walkthrough)),
	removeBuyerSubscriber: (obj, walkthrough) =>
		dispatch(removeBuyerSubscribersForWalkthroughs(ownProps.userType)(obj)(walkthrough)),
	removeSupplierSubscriber: (obj, walkthrough) =>
		dispatch(removeSupplierSubscribersForWalkthroughs(ownProps.userType)(obj)(walkthrough)),
});

export default withRouter<any, any>(
	withUserType(
		connect(mapStateToProps, mapDispatchToProps)(WalkthroughsDetailNotesPage as any) as any
	)
);
